import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeProfile } from '~/components/Auth';
import { useSelectedLanguageContext } from '~/components/SelectedLanguage';
import { CompanyDataLanguage, getCompanyData } from '~/data/company';
import { fetchEnums } from '~/data/enums';
import { useSWR } from '~/hooks/useSWR';
import { useVisible } from '~/hooks/useVisible';
import { useLibraryComponentContext } from '~/library/components/LibraryComponentContext';
import { AppDispatch } from '~/store';

import { putChangeLanguage } from './data';

export type UseChangeLanguageOutput = {
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  language: string;
  companyDataLanguages: CompanyDataLanguage[] | undefined;
  isChangeLanguageDialogOpen: boolean;
  isSubmitting: boolean;
  isCurrentLanguage: boolean;
  currentLanguage: string;
};

type Props = {
  resetDialog: () => void;
};

export const useChangeLanguage = (props: Props): UseChangeLanguageOutput => {
  const { resetDialog } = props;

  const dispatch: AppDispatch = useDispatch();

  const { onUpdateLegacy } = useLibraryComponentContext();

  const languageContext = useSelectedLanguageContext();

  const { data: companyData } = useSWR('getCompanyData', getCompanyData);
  const userProfile = useLoggedInEmployeeProfile();
  const [language, setLanguage] = useState<string>(
    languageContext.language ?? '',
  );
  const [isSubmitting, setSubmitting] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();

  const getCompanyLanguages = () => {
    if (companyData) {
      return companyData.languages;
    }
  };
  const companyDataLanguages = getCompanyLanguages();
  const {
    isVisible: isChangeLanguageDialogOpen,
    show: openChangeLanguageDialog,
    hide: closeChangeLanguageDialog,
  } = useVisible();

  const onChange = (value: string) => {
    setLanguage(value);
  };

  const onClose = useCallback(() => {
    closeChangeLanguageDialog();
    resetDialog();
  }, [closeChangeLanguageDialog, resetDialog]);

  useEffect(() => {
    openChangeLanguageDialog();
  }, [openChangeLanguageDialog]);

  const onSubmit = () => {
    (async () => {
      setSubmitting(true);

      return putChangeLanguage(language, userProfile.username as string)
        .then((response) => JSON.stringify(response).replace(/["']/g, ''))
        .then((data) => {
          languageContext.setLanguage(data);
          dispatch(fetchEnums());

          enqueueSnackbar(t('successLanguageChanged'), {
            variant: 'success',
          });
          if (onUpdateLegacy) {
            onUpdateLegacy(['changeLanguageUpdate']);
          }
          onClose();
        })
        .catch((_error) => {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        })
        .finally(() => {
          // Unmount handling
          if (!!setSubmitting && isSubmitting) {
            setSubmitting(false);
          }
        });
    })();
  };

  return {
    onChange,
    onSubmit,
    onClose,
    language,
    companyDataLanguages,
    isChangeLanguageDialogOpen,
    isSubmitting,
    isCurrentLanguage: languageContext.language === language,
    currentLanguage: languageContext.language ?? '',
  };
};
